/**
 * Future Cartridge battle HP bar — Phaser-wired DOM shell.
 *
 * Pure threshold + collision logic lives in
 *   apps/game/src/ui/hpBarColors.ts
 * Styles live in tokens.css under `.hp-bar` + `[data-threshold]` +
 * cinematic-effect classes (.is-sweeping{,-fast,-slow}, .is-flashing,
 * .is-shaking).
 *
 * One shell per bar — BattleScene mounts two (player bottom-left,
 * opponent top-right). Canvas-corner-anchored, NOT sprite-tracking,
 * per the design doc rev 6 — the new identity signal is the glass
 * material + type tint on the bar's container background, not which
 * sprite the bar is hovering above.
 *
 * BattleScene wire-up (PR 3/PR4) drives the shell via setHp /
 * setType / triggerSweep / triggerFlash / triggerShake on each
 * damage event. The shell handles SLEEP/WAKE (battle ends → battle
 * starts again later → bar persists) and viewport resize.
 */
import Phaser from 'phaser';
import {
  thresholdFor,
  isCritical,
  tintAlpha,
} from './hpBarColors';

const PARENT_ID = 'game';
const BAR_W = 80;
const BAR_H = 8;
const EDGE_INSET_TOP = 16;
/** Bottom inset clears Android gesture nav + iOS home indicator
 *  (parent.clientHeight = 100dvh on mobile can extend below the
 *  actually-visible area). */
const EDGE_INSET_BOTTOM = 100;
const EDGE_INSET_SIDE = 24;

export type BattleHpBarPosition = 'top-right' | 'bottom-left';

/** Hex string lookup for the 18 Future Cartridge type colors —
 *  mirrors tokens.css :root entries so we can compose rgba() for the
 *  type-tint inline custom property. */
const TYPE_HEX: Record<string, string> = {
  normal:   '#B6B5A2',
  fire:     '#FF4D29',
  water:    '#2E9EFF',
  electric: '#FFD126',
  grass:    '#5AD66E',
  ice:      '#7AD9DA',
  fighting: '#C13C46',
  poison:   '#B450C7',
  ground:   '#E5B956',
  flying:   '#A5BEF0',
  psychic:  '#FF5C82',
  bug:      '#A4BB39',
  rock:     '#B89B47',
  ghost:    '#6E5BA6',
  dragon:   '#7B5BFA',
  dark:     '#5F4F44',
  steel:    '#B8BAC4',
  fairy:    '#FF93C8',
};

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface BattleHpBarHandle {
  /** Update the fill width + threshold + critical sub-state from a
   *  HP fraction (0–1). Width transitions over 1 s ease-out via CSS;
   *  threshold + critical attributes update synchronously. Pass
   *  `snap: true` to bypass the transition for the duration of one
   *  animation frame — used by |switch| in BattleScene so the new
   *  active's HP appears instantly instead of animating from the
   *  previous active's value. */
  setHp(hpPct: number, snap?: boolean): void;
  /** Set the active Pokémon's primary type — drives the
   *  --hp-bar-type-tint custom property using the current threshold's
   *  collision-aware alpha. Re-applies on every setHp() too so the
   *  alpha tracks threshold transitions. */
  setType(type: string): void;
  /** Trigger the specular sweep keyframe. Speed scales with damage
   *  magnitude per design doc: 'slow' = < 10 % damage, 'normal' =
   *  10-30 %, 'fast' = > 30 %. Class is removed on animationend. */
  triggerSweep(speed: 'slow' | 'normal' | 'fast'): void;
  /** Trigger the 200 ms red overlay flash on > 50 % single-hit
   *  damage. Runs BEFORE the drain animation. */
  triggerFlash(): void;
  /** Trigger the 60 ms shake on > 30 % damage. Runs ON the bar
   *  container only (not the canvas). */
  triggerShake(): void;
  /** Mark the bar as fainted. Adds the `.is-fainting` class which
   *  drives the design-doc-spec'd 3× red pulse over 300 ms plus the
   *  type-tint drain. Sticky — the class stays on the element after
   *  the animation completes so the bar reads as permanently
   *  defeated for the rest of the battle. */
  triggerFaint(): void;
  /** Mark/unmark this bar's Pokémon as the player's last alive
   *  partner. Combined with `data-critical=true` (< 10 % HP) it
   *  enables the last-alive cinematic CSS — faster, deeper pulse. */
  setLastAlive(value: boolean): void;
  /** Unmount + remove all listeners. Idempotent. */
  destroy(): void;
}

export function mountBattleHpBar(
  scene: Phaser.Scene,
  opts: {
    position: BattleHpBarPosition;
    /** Inline mode mounts into `opts.parent` (or #game) at the parent's
     *  layout flow — no absolute positioning, no corner anchoring,
     *  no resize listeners. Used by battleStatusPanel.ts to compose
     *  the bar inside a containing glass surface. */
    inline?: boolean;
    /** Override default mount target. Defaults to #game in non-inline
     *  mode; required in inline mode to land in a flex/grid context. */
    parent?: HTMLElement;
    /** Inline mode only: grow to fill the flex row instead of sitting
     *  at a fixed 80 px. The fixed width wastes most of a container
     *  wider than it, which is the readability half of the owner's
     *  "the hp bar is not that useful" (2026-08-03). */
    fluidWidth?: boolean;
  },
): BattleHpBarHandle {
  const parent = opts.parent ?? document.getElementById(PARENT_ID) ?? document.body;

  const el = document.createElement('div');
  el.className = 'hp-bar';
  if (!opts.inline) el.style.position = 'absolute';
  if (opts.fluidWidth) {
    el.style.flex = '1 1 auto';
    el.style.width = 'auto';
  } else {
    el.style.width = `${BAR_W}px`;
  }
  el.style.height = `${BAR_H}px`;
  el.dataset.threshold = 'green';
  el.dataset.critical = 'false';

  const fill = document.createElement('div');
  fill.className = 'hp-bar__fill';
  el.appendChild(fill);

  const sweep = document.createElement('div');
  sweep.className = 'hp-bar__sweep';
  el.appendChild(sweep);

  parent.appendChild(el);

  let currentType = 'normal';
  let currentHpPct = 1.0;

  function position(): void {
    if (opts.position === 'top-right') {
      el.style.right = `${EDGE_INSET_SIDE}px`;
      el.style.top = `${EDGE_INSET_TOP}px`;
      el.style.left = '';
      el.style.bottom = '';
    } else {
      el.style.left = `${EDGE_INSET_SIDE}px`;
      el.style.bottom = `${EDGE_INSET_BOTTOM}px`;
      el.style.right = '';
      el.style.top = '';
    }
  }
  if (!opts.inline) position();

  function applyTypeTint(): void {
    const hex = TYPE_HEX[currentType.toLowerCase()] ?? TYPE_HEX.normal!;
    const alpha = tintAlpha(currentType, thresholdFor(currentHpPct));
    el.style.setProperty('--hp-bar-type-tint', hexToRgba(hex, alpha));
  }

  // ─── Resize handling — rAF-coalesced ─────────────────────────────────
  // In inline mode the parent container handles its own positioning;
  // no resize listener needed here.
  let resizeQueued = false;
  function onResize(): void {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => {
      resizeQueued = false;
      position();
    });
  }
  if (!opts.inline) {
    window.visualViewport?.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  }

  // ─── Phaser SLEEP/WAKE — hide DOM when scene swaps out ───────────────
  const onSleep = (): void => {
    el.style.display = 'none';
  };
  const onWake = (): void => {
    el.style.display = 'block';
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  // ─── Cinematic-effect class toggles ──────────────────────────────────
  function bindOneShot(className: string): () => void {
    return () => {
      el.classList.remove(className);
      // Force reflow so re-adding the class restarts the animation
      // even if it was already running. void el.offsetWidth is the
      // canonical CSS-restart trick.
      void el.offsetWidth;
      el.classList.add(className);
      const onEnd = (): void => {
        el.classList.remove(className);
        el.removeEventListener('animationend', onEnd);
      };
      el.addEventListener('animationend', onEnd);
    };
  }
  const triggerFlash = bindOneShot('is-flashing');
  const triggerShake = bindOneShot('is-shaking');

  function triggerSweep(speed: 'slow' | 'normal' | 'fast'): void {
    el.classList.remove('is-sweeping', 'is-sweeping-slow', 'is-sweeping-fast');
    void el.offsetWidth;
    const cls =
      speed === 'slow' ? 'is-sweeping-slow' :
      speed === 'fast' ? 'is-sweeping-fast' :
      'is-sweeping';
    el.classList.add(cls);
    const onEnd = (): void => {
      el.classList.remove(cls);
      sweep.removeEventListener('animationend', onEnd);
    };
    sweep.addEventListener('animationend', onEnd);
  }

  return {
    setHp(hpPct, snap = false): void {
      currentHpPct = Math.max(0, Math.min(1, hpPct));
      if (snap) {
        // Temporarily disable the CSS transition so the new width
        // applies instantly (no animation from the previous value).
        const prev = fill.style.transition;
        fill.style.transition = 'none';
        fill.style.width = `${currentHpPct * 100}%`;
        // Force layout so the snapped width commits before we restore
        // the transition declaration on the next animation frame.
        void fill.offsetWidth;
        requestAnimationFrame(() => {
          fill.style.transition = prev;
        });
      } else {
        fill.style.width = `${currentHpPct * 100}%`;
      }
      el.dataset.threshold = thresholdFor(currentHpPct);
      el.dataset.critical = isCritical(currentHpPct) ? 'true' : 'false';
      applyTypeTint();
    },
    setType(type): void {
      currentType = type;
      applyTypeTint();
    },
    triggerSweep,
    triggerFlash,
    triggerShake,
    triggerFaint(): void {
      el.classList.add('is-fainting');
    },
    setLastAlive(value): void {
      el.dataset.lastAlive = value ? 'true' : 'false';
    },
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      if (!opts.inline) {
        window.visualViewport?.removeEventListener('resize', onResize);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
      }
      el.remove();
    },
  };
}
